import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { CURRENT_CLIENT_TEMPLATE_VAR, defaultSelectOption, EntityStatus, utils } from '@core';
import { SessionStorageService } from '@core';
import { type FilterOption } from '@devices';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';

const optionsFilters = [
  {
    filter: 'deviceMaker',
    options: 'deviceMakers',
  },
  {
    filter: 'deviceType',
    options: 'deviceTypes',
  },
  {
    filter: 'building',
    options: 'locations',
  },
  {
    filter: 'floor',
    options: 'spaces',
  },
  {
    filter: 'room',
    options: 'spaces',
  },
];

export type FiltersOptions = {
  deviceMakers: FilterOption[];
  deviceTypes: FilterOption[];
  locations: FilterOption[];
  spaces: FilterOption[];
};

export interface FiltersState {
  building: string;
  deviceMaker: string[];
  deviceType: string[];
  floor: string;
  query: string;
  room: string;
  status: EntityStatus;
  tableId: string;
};

export const initialState: FiltersState = {
  building: '',
  deviceMaker: [],
  deviceType: [],
  floor: '',
  query: '',
  room: '',
  status: EntityStatus.Active,
  tableId: '',
};

export function withCommonState(filtersUrl: string) {
  return signalStoreFeature(
    withState(initialState),
    withState<{ _options: FiltersOptions; }>({
      _options: {
        deviceMakers: [],
        deviceTypes: [],
        locations: [],
        spaces: [],
      },
    }),
    withMethods((store, http = inject(HttpClient), sessionStorageService = inject(SessionStorageService)) => ({
      async fetchOptions(params?: { space: string; }) {
        const { data } = await firstValueFrom(this.filtersRequest<FiltersOptions>(params));

        patchState(store, () => ({ _options: data }));
      },
      filtersRequest<T>(params?: { space?: string; }) {
        return http.get<{ data: T; }>(
          `devices/v2/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/${filtersUrl}`,
          {
            params: {
              ...params?.space && { space: params.space },
            },
          },
        );
      },
      /**
       * Method removes filters that are not present in the options
       */
      verifyFilterOptions() {
        const verifiedFilters = {} as Record<string, string | string[]>;

        for (const { filter, options } of optionsFilters) {
          const filterValue = (store[filter as keyof FiltersState])();
          const filterOptions = store._options()![options as keyof FiltersOptions] as FilterOption[];

          if (Array.isArray(filterValue)) {
            verifiedFilters[filter] = filterValue.filter(value =>
              filterOptions.find(option => option.value === value),
            );

            continue;
          }

          const filterOption = filterOptions.find(option => option.value === filterValue);

          if (filterOption) {
            continue;
          }

          verifiedFilters[filter] = '';
        }

        this.patchFilters(verifiedFilters);
      },
      clearFilters(includeLocationFilters = true) {
        const filters = includeLocationFilters
          ? {
            building: '',
            deviceMaker: [],
            deviceType: [],
            floor: '',
            room: '',
          }
          : {
            deviceMaker: [],
            deviceType: [],
          };

        this.patchFilters(filters);
      },

      getSessionFilters() {
        const tableId = store.tableId();

        return tableId ? sessionStorageService.getFilters(tableId) : {};
      },

      initDefaultFilters(defaultFilters: Partial<Omit<FiltersState, 'tableId'>> = {}) {
        const sessionData = this.getSessionFilters() ?? {};

        this.patchFilters({ ...sessionData, ...defaultFilters });
      },

      onCheckboxChange(config: {
        checked: boolean;
        field: 'deviceType' | 'deviceMaker';
        value: string;
      }) {
        this.patchFilters({
          [config.field]: config.checked
            ? [...store[config.field](), config.value]
            : store[config.field]().filter(value => value !== config.value),
        });
      },

      patchFilters(value: Omit<Partial<FiltersState>, 'options' | 'tableId'>) {
        patchState(store, value);

        if (store.tableId()) {
          sessionStorageService.saveFiltersToSessionStorage(store.tableId(), {
            building: store.building(),
            deviceMaker: store.deviceMaker(),
            deviceType: store.deviceType(),
            floor: store.floor(),
            query: store.query(),
            room: store.room(),
            status: store.status(),
          });
        }
      },

      resetState() {
        patchState(store, () => ({ ...initialState }));
      },

      setTableId(tableId: string) {
        patchState(store, state => ({ ...state, tableId }));
      },
    })),
    withComputed(store => ({
      deviceMakerOptions: computed(() => store._options.deviceMakers()),
      deviceTypeOptions: computed(() => store._options.deviceTypes()),
      floorOptions: computed(() => [
        defaultSelectOption,
        ...store._options.spaces().filter(space => space.parentId === store.building()),
      ]),
      locationOptions: computed(() => [defaultSelectOption, ...store._options.locations()]),
      roomOptions: computed(() => [
        defaultSelectOption,
        ...store._options.spaces().filter(space => space.parentId === store.floor()),
      ]),
    }),
    ),
    withComputed(store => ({
      hasSelectedFilters: computed(() =>
        utils.hasValue({
          building: store.building(),
          deviceMaker: store.deviceMaker(),
          deviceType: store.deviceType(),
          floor: store.floor(),
          room: store.room(),
        }),
      ),
    })),
  );
}
